import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getMafertAppModulePermissions, getTenantModuleAccessSummary } from '../api/mafertappModulesApi';
import { colorTokens } from '../app/designTokens';
import {
  formatBillingStatus,
  formatModuleCode,
  formatReadinessMessage,
  formatSeedScope,
  formatSource,
  formatUnknownCode,
} from '../utils/systemLabels';
import { DataTable } from './DataTable';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';

const { semantic } = colorTokens;

function ModuleTable({ title, rows, t, columns }) {
  if (!rows?.length) {
    return null;
  }

  return (
    <SectionCard variant="Default" title={title} noPadding sx={{ mb: 0 }}>
      <DataTable
        type="Compact"
        embedded
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.module_code || row.id}
        emptyMessage={t('mafertappModules:no_modules')}
      />
    </SectionCard>
  );
}

export function TenantModuleAccessSection({ tenantId }) {
  const { t } = useTranslation(['mafertappModules', 'common', 'subscriptionChanges', 'permissionCleanup', 'features']);
  const [summary, setSummary] = useState(null);
  const [technicalPermissions, setTechnicalPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getTenantModuleAccessSummary(tenantId);
      setSummary(data);

      const coreModule = data.core_modules?.[0];
      if (coreModule?.id) {
        const perms = await getMafertAppModulePermissions(coreModule.id);
        setTechnicalPermissions(perms.data || []);
      }
    } catch {
      setError(t('common:load_failed'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const moduleColumns = useMemo(() => [
    {
      id: 'customer_name',
      label: t('mafertappModules:columns.customer_name'),
      render: (row) => row.customer_name
        || row.customer_name_sk
        || formatModuleCode(row.module_code)
        || row.module_code,
    },
    {
      id: 'billing_code',
      label: t('mafertappModules:billingCode'),
      render: (row) => row.billing_code || t('common:empty_value'),
    },
    {
      id: 'parent',
      label: t('mafertappModules:parentModule'),
      render: (row) => row.mafertapp_parent || t('common:empty_value'),
    },
    {
      id: 'permission_count',
      label: t('mafertappModules:permissionCount'),
      render: (row) => row.permission_count ?? t('common:empty_value'),
    },
    {
      id: 'seed_scope',
      label: t('mafertappModules:seedScope'),
      render: (row) => formatSeedScope(row.seed_scope || row.seed_policy?.seed_category)
        || t('common:empty_value'),
    },
    {
      id: 'source',
      label: t('mafertappModules:source'),
      render: (row) => formatSource(row.source || (row.is_core ? 'core' : null)) || t('common:empty_value'),
    },
  ], [t]);

  const technicalColumns = useMemo(() => [
    {
      id: 'label',
      label: t('common:label'),
      render: (row) => row.label || formatUnknownCode(row.label_key) || row.label_key,
    },
    {
      id: 'access_category',
      label: t('mafertappModules:accessCategory'),
      render: (row) => (
        <StatusBadge
          status={row.access_category || 'category'}
          category="generic"
          apiLabel={formatUnknownCode(row.access_category) || row.access_category}
          size="Sm"
          tone="Neutral"
        />
      ),
    },
    {
      id: 'view',
      label: t('features:columns.view'),
      render: (row) => row.permissions?.view || t('common:empty_value'),
    },
  ], [t]);

  if (loading) {
    return null;
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (!summary) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: semantic.text.primary,
          mb: -0.5,
        }}
      >
        {t('mafertappModules:title')}
      </Typography>

      <ModuleTable
        title={t('mafertappModules:sections.paid_modules')}
        rows={summary.active_paid_modules}
        t={t}
        columns={moduleColumns}
      />
      <ModuleTable
        title={t('mafertappModules:sections.core_modules')}
        rows={summary.core_modules}
        t={t}
        columns={moduleColumns}
      />
      <ModuleTable
        title={t('mafertappModules:sections.future_override')}
        rows={summary.future_override_modules}
        t={t}
        columns={moduleColumns}
      />
      <ModuleTable
        title={t('mafertappModules:sections.support_modules')}
        rows={summary.support_modules}
        t={t}
        columns={moduleColumns}
      />

      {(summary.permission_cleanup?.pending_cleanup_exists || summary.permission_cleanup_required_preview?.enabled) && (
        <Alert severity="warning">
          {t('permissionCleanup:permissionCleanupRequired')}
          {summary.permission_cleanup?.next_effective_at && (
            <> — {t('permissionCleanup:effectiveAt')}: {new Date(summary.permission_cleanup.next_effective_at).toLocaleString()}</>
          )}
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            {formatReadinessMessage({
              message_key: summary.permission_cleanup_required_preview?.message_key,
              message: summary.permission_cleanup_required_preview?.message,
            })
              || t('permissionCleanup:previewOnly')}
          </Typography>
        </Alert>
      )}

      {(summary.subscription_changes?.pending || []).length > 0 && (
        <Alert severity="warning">
          {t('subscriptionChanges:pendingChanges')}: {(summary.subscription_changes.pending || []).map((change) => (
            `${formatUnknownCode(change.change_type)} (${formatBillingStatus(change.status) || formatUnknownCode(change.status)})`
          )).join(', ')}
          {summary.subscription_changes.next_downgrade_effective_at && (
            <> — {t('subscriptionChanges:nextDowngrade')}: {new Date(summary.subscription_changes.next_downgrade_effective_at).toLocaleString()}</>
          )}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ mb: 0 }}>
        <Accordion
          defaultExpanded={false}
          disableGutters
          elevation={0}
          sx={{
            bgcolor: 'transparent',
            backgroundImage: 'none',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: semantic.text.muted }} />}
            sx={{
              minHeight: 48,
              px: 2.5,
              '& .MuiAccordionSummary-content': { my: 1.25 },
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: semantic.text.primary,
              }}
            >
              {t('mafertappModules:sections.technical_permissions')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
            <DataTable
              type="Compact"
              embedded
              columns={technicalColumns}
              rows={technicalPermissions.slice(0, 20)}
              getRowKey={(row) => row.id}
              emptyMessage={t('mafertappModules:no_modules')}
            />
          </AccordionDetails>
        </Accordion>
      </SectionCard>

      <Alert severity="info">
        {t('permissionCleanup:permissionCleanupPreview')}:{' '}
        {formatReadinessMessage({
          message_key: summary.permission_cleanup_required_preview?.message_key,
          message: summary.permission_cleanup_required_preview?.message,
        })
          || t('mafertappModules:cleanup_placeholder')}
      </Alert>
    </Box>
  );
}
